import { useEffect, useRef, useState } from "react";
import useWebPost from "../../utils/useWebPost";
import ImgUploader from "../../components/ImgUploader";
import { Button, Table, Form, Input, message, Space, Popconfirm, Pagination } from "antd";
import renderFileList from "../../components/renderFileList";
import { checkp, getobjkeys } from "../../utils/functions";
import CustModal from "../../components/ShowModal";
import { PlusOutlined } from "@ant-design/icons";
import FullHeightLayout from "../../components/FullHeightLayout";
import DateInput from "../../components/DateInput";

const { TextArea } = Input;

function InstallationList() {
    const webPost = useWebPost();
    const [kw, setKw] = useState('');
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(1);
    const [loading, setLoading] = useState(true);
    const [currentItem, setCurrentItem] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [form] = Form.useForm();
    const loadingRef = useRef(false);
    const pageSizeOptions = [10, 20, 50, 100, 500];

    const columns = [
        { title: '日期', dataIndex: 'record_date', width: 120 },
        { title: '项目名称', dataIndex: 'project_name', width: 180 },
        {
            title: '施工合同',
            dataIndex: 'contract',
            render: (file) => renderFileList(file)
        },
        {
            title: '施工方案',
            dataIndex: 'construction_plan',
            render: (file) => renderFileList(file)
        },
        {
            title: '设计图纸',
            dataIndex: 'design_drawing',
            render: (file) => renderFileList(file)
        },
        {
            title: '质量管理人员任命书',
            dataIndex: 'quality_manage_letter',
            render: (file) => renderFileList(file)
        },
        {
            title: '合格焊工登记表',
            dataIndex: 'qualified_welder_table',
            render: (file) => renderFileList(file)
        },
        {
            title: '管道材料证明',
            dataIndex: 'pipe_material_cert',
            render: (file) => renderFileList(file)
        },
        {
            title: '焊接材料证明',
            dataIndex: 'weld_material_cert',
            render: (file) => renderFileList(file)
        },
        {
            title: '管道焊接记录',
            dataIndex: 'pipe_weld_record',
            render: (file) => renderFileList(file)
        },
        {
            title: '焊缝探伤记录',
            dataIndex: 'weld_test_record',
            render: (file) => renderFileList(file)
        },
        {
            title: '试压（吹扫）记录',
            dataIndex: 'pipe_test_record',
            render: (file) => renderFileList(file)
        },
        {
            title: '告知书',
            dataIndex: 'notice_doc',
            render: (file) => renderFileList(file)
        },
        {
            title: '竣工图',
            dataIndex: 'complete_drawing',
            render: (file) => renderFileList(file)
        },
        {
            title: '验收报告',
            dataIndex: 'accept_report',
            render: (file) => renderFileList(file)
        },
        {
            title: '监督检验证书',
            dataIndex: 'inspect_cert',
            render: (file) => renderFileList(file)
        },
        { title: '说明', dataIndex: 'remark', width: 200 },
        checkp('tszm') && {
            title: '操作',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button onClick={() => handleEdit(record)}>
                        修改
                    </Button>
                    <Popconfirm
                        title="确定删除该条数据吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" danger>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ].filter(Boolean);

    // 获取列表数据
    useEffect(() => {
        fetchList();
    }, []);

    const fetchList = async (_page, _pagesize) => {
        setLoading(true);
        try {
            const params = { page: _page || page, limit: _pagesize || pageSize, kw };
            const res = await webPost('getInstallationPerformance', params);
            if (res && res.data) {
                setData(res.data);
                setTotal(res.total);
            }
        } catch (err) {
            setData([]);
            setTotal(0);
            message.error('数据加载失败');
        } finally {
            setLoading(false);
        }
    };

    // 新增
    const handleAdd = () => {
        setCurrentItem(null);
        setModalVisible(true);
        form.resetFields();
    };

    // 修改
    const handleEdit = (record) => {
        setCurrentItem(record);
        setModalVisible(true);
        form.setFieldsValue(record);
    };

    // 删除
    const handleDelete = async (id) => {
        try {
            const res = await webPost('deleteInstallationPerformance', { id });
            if (res) {
                message.success('删除成功');
                fetchList();
            }
        } catch (err) {
            message.error('删除失败');
        }
    };

    // 提交保存
    const handleSubmit = async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;

        try {
            const values = await form.validateFields();
            const params = { ...values };

            if (currentItem) {
                params.id = currentItem.id;
            }

            const res = await webPost('saveInstallationPerformance', params);
            if (res) {
                message.success(currentItem ? '修改成功' : '新增成功');
                setModalVisible(false);
                fetchList();
            }
        } catch (err) {
            message.error('保存失败：' + err.message);
        } finally {
            loadingRef.current = false;
        }
    };

    const onPageChange = (page, size) => {
        setPage(page);
        if (size !== pageSize) setPageSize(size);
        fetchList(page, size);
    };

    return (
        <FullHeightLayout
            headerContent={<Space>
                <Input
                    placeholder="搜索项目名称"
                    onChange={(e) => setKw(e.target.value)}
                    style={{ width: 260 }}
                />
                <Button type="primary" onClick={fetchList} >搜索</Button>
                <Button
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    style={{ position: "absolute", zIndex: 10, right: 16, top: 8 }}
                >
                    新增安装业绩
                </Button>
            </Space>}
            bodyContent={null}
            footerContent={<div style={{
                display: 'flex', justifyContent: 'flex-end',
                alignItems: 'center',
            }}>
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(_total, range) => `第 ${range[0]}-${range[1]} 条，共 ${_total} 条`}
                    onChange={onPageChange}
                    pageSizeOptions={pageSizeOptions}
                />
            </div>}
        >
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ x: 3000 }}
            />

            <CustModal
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={900}
            >
                <Form form={form} layout="vertical" labelAlign="left">
                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item
                            label="日期"
                            name="record_date"
                            rules={[{ required: true, message: '请选择日期' }]}
                            style={{ flex: 1 }}
                        >
                            <DateInput />
                        </Form.Item>
                        <Form.Item
                            label="项目名称"
                            name="project_name"
                            rules={[{ required: true, message: '请输入项目名称' }]}
                            style={{ flex: 2 }}
                        >
                            <Input placeholder="请输入项目名称" />
                        </Form.Item>
                    </div>

                    <Form.Item label="施工合同" name="contract">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="施工方案" name="construction_plan">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="设计图纸（盖章最终版本）" name="design_drawing">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="质量管理人员任命书" name="quality_manage_letter">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="合格焊工登记表" name="qualified_welder_table">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="管道材料质量证明文件" name="pipe_material_cert">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="焊接材料质量证明文件" name="weld_material_cert">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="管道焊接记录" name="pipe_weld_record">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="焊缝探伤记录" name="weld_test_record">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="管道试压（吹扫）记录" name="pipe_test_record">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="告知书" name="notice_doc">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="竣工图" name="complete_drawing">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="验收报告" name="accept_report">
                        <ImgUploader />
                    </Form.Item>
                    <Form.Item label="监督检验证书" name="inspect_cert">
                        <ImgUploader />
                    </Form.Item>

                    <Form.Item label="说明" name="remark">
                        <TextArea rows={3} placeholder="补充说明" />
                    </Form.Item>
                </Form>
            </CustModal>
        </FullHeightLayout>
    );
}

export default InstallationList;